import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Calendar, Tag, User } from 'lucide-react';
import { Reveal } from '@/components/Reveal';

const API = '/api';

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function InsightDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/blog/${slug}`)
      .then(r => {
        setPost(r.data);
        document.title = `${r.data.title} | NKP VENTURES Insights`;
      })
      .catch(() => navigate('/insights'))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7FAFC] pt-16">
        <div className="w-10 h-10 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!post) return null;

  return (
    <div>
      {/* ── Hero ── Unified light section with breadcrumb integrated */}
      <section className="pt-28 pb-16 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Inline breadcrumb */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-xs text-[#9CA3AF] font-sans mb-8"
          >
            <Link to="/" className="hover:text-[#0E5A57] transition-colors duration-150">Home</Link>
            <span>/</span>
            <Link to="/insights" className="hover:text-[#0E5A57] transition-colors duration-150">Insights</Link>
            <span>/</span>
            <span className="text-[#6B7280] line-clamp-1 max-w-xs">{post.title}</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 text-[#0E5A57] hover:text-[#2E7D7A] text-sm mb-7 transition-colors duration-150"
            >
              <ArrowLeft className="w-4 h-4" /> All Insights
            </Link>

            {post.category && (
              <div className="mb-6">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0E5A57] bg-teal-50 border border-teal-100 px-3 py-1.5 rounded-full font-sans">
                  <Tag className="w-3 h-3" /> {post.category}
                </span>
              </div>
            )}

            <h1 className="font-serif text-4xl lg:text-5xl font-bold text-[#153E5C] leading-tight mb-6">
              {post.title}
            </h1>

            <p className="text-[#6B7280] text-lg font-sans leading-relaxed mb-8 max-w-3xl">
              {post.summary}
            </p>

            <div className="flex items-center gap-6 pt-6 border-t border-[#F3F4F6]">
              <div className="flex items-center gap-2 text-sm text-[#9CA3AF] font-sans">
                <div className="w-7 h-7 bg-teal-50 border border-teal-100 rounded-full flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-[#0E5A57]" />
                </div>
                {post.author || 'NKP VENTURES Editorial Team'}
              </div>
              <div className="flex items-center gap-2 text-sm text-[#9CA3AF] font-sans">
                <Calendar className="w-4 h-4" />
                {formatDate(post.published_at || post.created_at)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Article Content ── */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="prose-pkepl" dangerouslySetInnerHTML={{ __html: post.content }} />
          </Reveal>
        </div>
      </section>

      {/* ── Tags ── */}
      {post.tags && post.tags.length > 0 && (
        <section className="py-7 bg-white border-t border-[#E5E7EB]">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 flex items-center gap-3 flex-wrap">
            <span className="text-xs text-[#9CA3AF] font-mono uppercase tracking-wider">Topics:</span>
            {post.tags.map(tag => (
              <span key={tag} className="text-xs text-[#374151] bg-[#F7FAFC] border border-[#E5E7EB] px-3 py-1.5 rounded-full font-sans hover:border-[#0E5A57]/25 hover:text-[#0E5A57] transition-colors duration-150 cursor-default">
                {tag}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <section className="py-28 bg-[#0E5A57]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="overline-label text-teal-300 mb-4">Apply These Insights</p>
                <h2 className="font-serif text-4xl font-bold text-white mb-4 leading-tight">
                  Ready to optimise your<br />business operations?
                </h2>
                <p className="text-white/60 font-sans leading-relaxed max-w-md">
                  Talk to our team about putting these ideas into practice with dedicated operational support.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
                <Link to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#153E5C] font-bold px-9 py-4 rounded-full transition-all duration-200 group">
                  Schedule a Consultation
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                </Link>
                <Link to="/insights"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/50 text-white font-medium px-9 py-4 rounded-full transition-all duration-200 text-sm">
                  <ArrowLeft className="w-4 h-4" /> More Insights
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
